/**
 * @fileoverview Render the admin Phone Dashboard with Live, Overview, People, Calls and Definitions views. The view, date range, page, filters and open call live in the URL so a link reopens the same screen; the live view polls the server while it is shown. Call detail belongs to `kc-internal-frontend/src/components/Admin/PhoneCallModal.js` and shared formatting to `kc-internal-frontend/src/components/Admin/phoneDashboardFormat.js`.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowDown, ArrowUp, ArrowsLeftRight, BookOpen, Broadcast, CaretDown, CaretLeft, CaretRight, ChartBar,
  MagnifyingGlass, Phone, PhoneIncoming, PhoneOutgoing, SpeakerHigh, Users, X,
} from '@phosphor-icons/react';

import api from '../../api';
import ToastContext from '../../ToastContext';
import { AppButton, AppSelect, AppSpinner, AppTextInput, AppToggle, ChartCanvas } from '../shared';
import { AdminEmptyState } from './AdminChrome';
import PhoneCallModal, { ClientBadge, LiveCallModal, StatusChip } from './PhoneCallModal';
import {
  daysBetween, firmDate, firmDay, formatClock, formatDate, formatDay, formatPhone, formatSeconds, formatTimer, partyTitle,
  shiftDate,
} from './phoneDashboardFormat';
import './PhoneDashboard.css';

const PAGE_SIZE = 50;
const LIVE_POLL_MS = 5000;
// The phone server refreshes the live list at least every 15 seconds; older than this, it may be out of date.
const LIVE_STALE_MS = 60 * 1000;
const VIEWS = [
  { id: 'live', label: 'Live', Icon: Broadcast },
  { id: 'overview', label: 'Overview', Icon: ChartBar },
  { id: 'people', label: 'People', Icon: Users },
  { id: 'calls', label: 'Calls', Icon: Phone },
  { id: 'definitions', label: 'Definitions', Icon: BookOpen },
];
const FILTER_KEYS = ['q', 'staff', 'outcome', 'direction', 'intake', 'recorded'];
// The filters behind the Calls view's "Filters" button.
const MORE_FILTERS = ['staff', 'direction', 'intake', 'recorded'];

// Caller perspective: how every incoming call ended for the caller.
const CALLER = [
  { id: 'answer', label: 'Answered' },
  { id: 'voicemail', label: 'Voicemail' },
  { id: 'missed', label: 'Missed' },
  { id: 'left', label: 'Left early' },
  { id: 'selfservice', label: 'Self service' },
  { id: 'disconnect', label: 'Disconnected' },
];
const DIRECTION_OPTIONS = [
  { value: 'inbound', label: 'Incoming' },
  { value: 'outbound', label: 'Outgoing' },
  { value: 'local', label: 'Internal' },
];

// Intake perspective: what one agent's phone did for one call.
const AGENT_LABELS = {
  answered: 'Answered',
  passed_quick: 'Passed < 10s',
  passed_slow: 'Passed 10s+',
  missed_by_everyone: 'Missed by everyone',
  declined: 'Declined',
  busy: 'Busy',
  offline: 'Offline',
  caller_left: 'Caller hung up',
  logged_out: 'Logged out',
  dnd: 'DND',
  out_of_hours: 'Out of hours',
  not_rung: 'Not rung',
};
const COLORS = { answered: '#16a34a', voicemail: '#0284c7', abandoned: '#dc2626' };

// ------------------------------------------------------------------ URL state

function usePhoneDashboardParams() {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => ({
    view: VIEWS.some((v) => v.id === params.get('view')) ? params.get('view') : 'overview',
    from: params.get('from') || firmDate(-6),
    to: params.get('to') || firmDate(),
    page: Math.max(Number(params.get('page')) || 1, 1),
    call: params.get('call') ? Number(params.get('call')) : null,
    live: params.get('live') || null,
    filters: Object.fromEntries(FILTER_KEYS.map((key) => [key, params.get(key) || ''])),
  }), [params]);

  const update = useCallback((changes, { replace = false } = {}) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined || value === false) next.delete(key);
        else next.set(key, value === true ? '1' : String(value));
      });
      return next;
    }, { replace });
  }, [setParams]);

  return [state, update];
}

function lineName(call) {
  if (!call.line) return '';
  return call.line_is_main ? 'Main line' : call.line;
}

function firstName(name) {
  return (name || '').split(' ')[0];
}

function Direction({ direction }) {
  const title = direction === 'outbound' ? 'Outgoing' : direction === 'inbound' ? 'Incoming' : 'Internal';
  return (
    <span className={`pd-dir pd-dir--${direction}`} title={title}>
      {direction === 'outbound' ? <PhoneOutgoing size={15} /> : direction === 'inbound' ? <PhoneIncoming size={15} /> : <ArrowsLeftRight size={15} />}
    </span>
  );
}

// ------------------------------------------------------------------ overview

function Delta({ current, previous, show, days, invert = false, points = false }) {
  if (!show || previous === null || previous === undefined || current === null || current === undefined) return null;
  const change = points ? current - previous : previous ? Math.round(((current - previous) / previous) * 100) : null;
  if (change === null) return null;
  if (change === 0) return <span className="pd-delta">Same as the previous {days} days</span>;
  const good = invert ? change < 0 : change > 0;
  return (
    <span className={`pd-delta ${good ? 'is-good' : 'is-bad'}`}>
      {change > 0 ? <ArrowUp size={12} weight="bold" aria-hidden="true" /> : <ArrowDown size={12} weight="bold" aria-hidden="true" />}
      {Math.abs(change)}{points ? ' pts' : '%'} vs previous {days} days
    </span>
  );
}

function Tile({ label, value, hint, children, onClick }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag className={`pd-tile${onClick ? ' is-clickable' : ''}`} onClick={onClick} type={onClick ? 'button' : undefined}>
      <span className="pd-tile__label">{label}</span>
      <strong className="pd-tile__value">{value}</strong>
      {hint ? <span className="pd-tile__hint">{hint}</span> : null}
      {children}
    </Tag>
  );
}

function chartTheme() {
  const root = getComputedStyle(document.documentElement);
  const read = (name, fallback) => root.getPropertyValue(name).trim() || fallback;
  return {
    text: read('--text-color', '#f3f4f6'),
    muted: read('--text-color-secondary', '#cbd5e1'),
    surface: read('--kc-surface-section', '#152536'),
    inset: read('--kc-surface-inset', '#0c1723'),
    edge: read('--kc-surface-edge', '#203044'),
    font: getComputedStyle(document.body).fontFamily,
  };
}

// A step lighter, for the day under the pointer.
function lighten(hex, amount = 0.18) {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgb(${[r, g, b].map((v) => Math.round(v + (255 - v) * amount)).join(', ')})`;
}

const SERIES = [
  { key: 'answered', label: 'Answered' },
  { key: 'voicemail', label: 'Voicemail' },
  { key: 'abandoned', label: 'Abandoned' },
];
// Day totals above the columns only while they have room to stay readable.
const TOTALS_UP_TO_DAYS = 14;

function DailyChart({ daily }) {
  const theme = useMemo(chartTheme, []);
  const totals = useMemo(() => daily.map((d) => SERIES.reduce((sum, s) => sum + d[s.key], 0)), [daily]);
  const data = useMemo(() => {
    const short = daily.length <= TOTALS_UP_TO_DAYS;
    // The highest and lowest non-empty segment of each day: the top one gets the rounded cap,
    // every one above the lowest gets the 2px gap beneath it.
    const top = daily.map((d) => [...SERIES].reverse().find((s) => d[s.key] > 0)?.key);
    const bottom = daily.map((d) => SERIES.find((s) => d[s.key] > 0)?.key);
    return {
      labels: daily.map((d) => {
        const day = new Date(`${d.date}T12:00:00`);
        const date = day.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
        return short ? [day.toLocaleDateString('en-US', { weekday: 'short' }), date] : date;
      }),
      datasets: SERIES.map((s) => ({
        label: s.label,
        data: daily.map((d) => d[s.key]),
        backgroundColor: COLORS[s.key],
        hoverBackgroundColor: lighten(COLORS[s.key]),
        stack: 'intake',
        maxBarThickness: 24,
        categoryPercentage: 0.7,
        borderSkipped: false,
        borderColor: theme.surface,
        hoverBorderColor: theme.surface,
        borderWidth: (ctx) => (bottom[ctx.dataIndex] && bottom[ctx.dataIndex] !== s.key ? { bottom: 2 } : 0),
        borderRadius: (ctx) => (top[ctx.dataIndex] === s.key ? { topLeft: 4, topRight: 4 } : 0),
      })),
    };
  }, [daily, theme]);

  const options = useMemo(() => ({
    maintainAspectRatio: false,
    layout: { padding: { top: 20 } },
    interaction: { mode: 'index', intersect: false },
    font: { family: theme.font },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: theme.inset,
        borderColor: theme.edge,
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        titleColor: theme.muted,
        titleFont: { family: theme.font, size: 12, weight: '600' },
        bodyColor: theme.text,
        bodyFont: { family: theme.font, size: 13 },
        footerColor: theme.muted,
        footerFont: { family: theme.font, size: 12, weight: '500' },
        footerMarginTop: 8,
        filter: (item) => item.raw > 0,
        callbacks: {
          // Read the day from the tooltip itself: on a day with no calls the filter leaves no items.
          title() {
            const index = this.getActiveElements()[0]?.index;
            return index === undefined ? '' : new Date(`${daily[index].date}T12:00:00`)
              .toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
          },
          label: (item) => `${item.raw}  ${item.dataset.label.toLowerCase()}`,
          labelPointStyle: () => ({ pointStyle: 'line', rotation: 0 }),
          labelColor: (item) => ({ borderColor: COLORS[SERIES[item.datasetIndex].key], backgroundColor: COLORS[SERIES[item.datasetIndex].key], borderWidth: 3 }),
          footer() {
            const index = this.getActiveElements()[0]?.index;
            if (index === undefined) return '';
            const total = totals[index];
            return total ? `${total} intake call${total === 1 ? '' : 's'} · ${Math.round((100 * daily[index].answered) / total)}% answered` : 'No intake calls';
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        border: { color: theme.edge },
        ticks: { color: theme.muted, maxRotation: 0, autoSkip: true, autoSkipPadding: 24, font: { family: theme.font, size: 12 } },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: theme.edge, lineWidth: 1, drawTicks: false },
        border: { display: false },
        ticks: { color: theme.muted, precision: 0, padding: 10, maxTicksLimit: 5, font: { family: theme.font, size: 12 } },
      },
    },
  }), [daily, theme, totals]);

  // The day's total on top of each column, in text colours rather than the series colours.
  const plugins = useMemo(() => [{
    id: 'dayTotals',
    afterDatasetsDraw(chart) {
      if (daily.length > TOTALS_UP_TO_DAYS) return;
      const { ctx } = chart;
      const last = chart.getDatasetMeta(SERIES.length - 1).data;
      ctx.save();
      ctx.font = `600 12px ${theme.font}`;
      ctx.fillStyle = theme.muted;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      totals.forEach((total, index) => {
        if (!total) return;
        const top = Math.min(...SERIES.map((_, i) => chart.getDatasetMeta(i).data[index].y));
        ctx.fillText(String(total), last[index].x, top - 6);
      });
      ctx.restore();
    },
  }], [daily, theme, totals]);

  const total = totals.reduce((sum, n) => sum + n, 0);
  return (
    <div className="pd-chart" role="img"
      aria-label={`Intake calls per day: ${daily.map((d, i) => `${d.date} ${totals[i]}`).join(', ')}. ${total} in all.`}>
      <ChartCanvas type="bar" data={data} options={options} plugins={plugins} />
    </div>
  );
}

function CalledBack({ call }) {
  const back = call.called_back;
  if (!back) return <span className="pd-faint">no</span>;
  const sameDay = formatDay(back.at) === formatDay(call.start_time);
  const when = `${sameDay ? '' : `${formatDay(back.at)} `}${formatClock(back.at)}`;
  return (
    <span className="pd-good" title={back.outbound ? `${back.staff_name || 'Staff'} called them back` : 'They called again and reached someone'}>
      {back.outbound ? when : `got through ${when}`}
    </span>
  );
}

const UNREACHED_PREVIEW = 5;

function Unreached({ calls, days, openCall }) {
  const [all, setAll] = useState(false);
  const shown = all ? calls : calls.slice(0, UNREACHED_PREVIEW);
  return (
    <section className="pd-card">
      <header className="pd-card__header">
        <h3>New clients who never reached anyone</h3>
        <span>{calls.length} {days === 1 ? 'today' : `in these ${days} days`}{calls.length ? ' · newest first' : ''}</span>
      </header>
      {calls.length === 0 ? <p className="pd-pad pd-muted">Every caller who chose a new case reached someone.</p> : (
        <>
          <div className="pd-scroll">
            <table className="pd-table pd-calls">
              <thead>
                <tr><th>When</th><th>Who</th><th>What happened</th><th>Line</th><th className="pd-num">Called back</th></tr>
              </thead>
              <tbody>
                {shown.map((call) => {
                  const title = partyTitle(call);
                  const number = formatPhone(call.party_number);
                  return (
                    <tr key={call.id} className="is-clickable" onClick={() => openCall(call.id)}>
                      <td><span className="pd-when"><span className="pd-faint">{formatDay(call.start_time)}</span> {formatClock(call.start_time)}</span></td>
                      <td><span className="pd-who">
                        <span className="pd-person">{title}</span>
                        {title !== number && <span className="pd-faint">{number}</span>}
                        {call.client && <ClientBadge client={call.client} compact />}
                      </span></td>
                      <td className="pd-fill"><StatusChip status={call.status} label={call.status_label} /></td>
                      <td className="pd-muted pd-line">{lineName(call)}</td>
                      <td className="pd-num"><CalledBack call={call} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {calls.length > UNREACHED_PREVIEW && (
            <button type="button" className="pd-card__more" onClick={() => setAll(!all)}>
              {all ? 'Show fewer' : `Show all ${calls.length}`} {all ? <CaretDown size={13} aria-hidden="true" /> : <CaretRight size={13} aria-hidden="true" />}
            </button>
          )}
        </>
      )}
    </section>
  );
}

function Overview({ stats, openCalls, openCall }) {
  const intake = stats.intake;
  const prev = stats.previous_intake;
  const show = stats.previous_complete;
  const days = stats.days;
  return (
    <div className="pd-stack">
      <div className="pd-tiles">
        <Tile label="Answered" value={intake.answer_rate !== null ? `${intake.answer_rate}%` : '—'}
          hint={`${intake.answered} of ${intake.calls} intake calls`}>
          <Delta current={intake.answer_rate} previous={prev.answer_rate} show={show} days={days} points />
        </Tile>
        <Tile label={`Answered within ${intake.answer_target_seconds}s`}
          value={intake.answered_quickly_rate !== null ? `${intake.answered_quickly_rate}%` : '—'}
          hint={`${intake.answered_quickly} of ${intake.calls}`}>
          <Delta current={intake.answered_quickly_rate} previous={prev.answered_quickly_rate} show={show} days={days} points />
        </Tile>
        <Tile label="Abandoned in queue" value={intake.abandoned}
          hint={intake.avg_wait_before_giving_up !== null ? `after ${formatSeconds(intake.avg_wait_before_giving_up)} on average` : 'Nobody abandoned'}
          onClick={intake.abandoned ? () => openCalls({ outcome: 'missed', intake: true }) : undefined}>
          <Delta current={intake.abandoned} previous={prev.abandoned} show={show} days={days} invert />
        </Tile>
        <Tile label="Average wait" value={formatSeconds(intake.avg_wait)}
          hint={intake.longest_wait !== null ? `longest ${formatSeconds(intake.longest_wait)}` : null}>
          <Delta current={intake.avg_wait} previous={prev.avg_wait} show={show} days={days} invert />
        </Tile>
        <Tile label="No agent free" value={intake.no_agent_free}
          hint="sent straight to the new client menu"
          onClick={intake.no_agent_free ? () => openCalls({ intake: 'no_agent_free' }) : undefined}>
          <Delta current={intake.no_agent_free} previous={prev.no_agent_free} show={show} days={days} invert />
        </Tile>
      </div>

      <Unreached calls={stats.unreached} days={days} openCall={openCall} />

      <section className="pd-card">
        <header className="pd-card__header">
          <h3>Intake calls per day</h3>
          <span className="pd-legend">
            {[['Answered', COLORS.answered], ['Voicemail', COLORS.voicemail], ['Abandoned', COLORS.abandoned]].map(([label, color]) => (
              <span key={label}><span className="pd-swatch" style={{ background: color }} />{label}</span>
            ))}
          </span>
        </header>
        {stats.daily.length ? <DailyChart daily={stats.daily} /> : null}
      </section>

      {!show && stats.history_start && (
        <p className="pd-footnote">Call history starts {stats.history_start}, so changes from the previous period are hidden for this range.</p>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ people

const GROUPS = [
  { id: 'intake', label: 'Intake team', has: (p) => p.intake },
  { id: 'attorney', label: 'Attorneys', has: (p) => p.attorney },
  { id: 'everyone', label: 'Everyone', has: () => true },
];
const PICK_KEY = 'kc.phoneDashboard.people';

// Who the People view shows: a group and people added one at a time. Remembered on this browser.
function usePeoplePick() {
  const [pick, setPick] = useState(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(PICK_KEY));
      return saved && Array.isArray(saved.people) ? saved : null;
    } catch {
      return null;
    }
  });
  const save = useCallback((next) => {
    setPick(next);
    try { window.localStorage.setItem(PICK_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  }, []);
  return [pick, save];
}

function hasCalls(p) {
  return p.answered || p.missed_by_everyone || p.declined || p.passed_quick || p.passed_slow || p.outbound || p.voicemails;
}

function PeoplePicker({ pick, setPick, staff, shown }) {
  const group = pick?.group || null;
  const people = pick?.people || [];
  const others = staff.filter((p) => !shown.has(p.extension));
  return (
    <>
      {GROUPS.map((g) => (
        <button key={g.id} type="button" className={`pd-btn${group === g.id ? ' is-active' : ''}`} aria-pressed={group === g.id}
          onClick={() => setPick({ group: group === g.id ? null : g.id, people })}>{g.label}</button>
      ))}
      <select className="pd-add-person" aria-label="Add a person" value=""
        onChange={(event) => event.target.value && setPick({ group, people: [...people, event.target.value] })}>
        <option value="">+ Add a person…</option>
        {others.map((p) => <option key={p.extension} value={p.extension}>{p.name || `Ext ${p.extension}`}</option>)}
      </select>
      {people.map((ext) => {
        const person = staff.find((p) => p.extension === ext);
        return (
          <span key={ext} className="pd-person-chip">
            {person?.name || `Ext ${ext}`}
            <button type="button" aria-label={`Remove ${person?.name || ext}`}
              onClick={() => setPick({ group, people: people.filter((e) => e !== ext) })}><X size={12} /></button>
          </span>
        );
      })}
    </>
  );
}

function People({ stats, staff, openPerson }) {
  const [pick, setPick] = usePeoplePick();
  const byExt = useMemo(() => new Map(stats.people.map((p) => [p.extension, p])), [stats.people]);
  const group = GROUPS.find((g) => g.id === pick?.group);
  const rows = useMemo(() => {
    const found = new Map();
    if (group) {
      stats.people.filter((p) => group.has(p) && (p.intake || hasCalls(p))).forEach((p) => found.set(p.extension, p));
    }
    (pick?.people || []).forEach((ext) => {
      if (found.has(ext)) return;
      const person = staff.find((p) => p.extension === ext);
      found.set(ext, byExt.get(ext) || { extension: ext, name: person?.name, intake: person?.intake, empty: true });
    });
    return [...found.values()].sort((a, b) => ((b.answered || 0) + (b.outbound || 0)) - ((a.answered || 0) + (a.outbound || 0)));
  }, [group, pick, stats.people, staff, byExt]);

  if (!pick || (!pick.group && !pick.people.length)) {
    return (
      <section className="pd-card pd-empty">
        <h3>Pick people to compare</h3>
        <p className="pd-muted">Choose a group, or add people one at a time.</p>
        <div className="pd-picker pd-picker--center">
          <PeoplePicker pick={pick} setPick={setPick} staff={staff} shown={new Set()} />
        </div>
      </section>
    );
  }

  const intakeColumns = rows.some((p) => p.intake);
  const count = (value) => value || 0;
  return (
    <section className="pd-card">
      <div className="pd-picker">
        <span className="pd-muted">Show</span>
        <PeoplePicker pick={pick} setPick={setPick} staff={staff} shown={new Set(rows.map((p) => p.extension))} />
      </div>
      {rows.length === 0 ? <div className="pd-pad"><AdminEmptyState title="No calls for these people in this range" /></div> : (
        <div className="pd-scroll">
          <table className="pd-table pd-table--tight">
            <thead>
              <tr>
                <th>Person</th>
                <th className="pd-num" title="Incoming calls they picked up">Answered</th>
                <th className="pd-num" title="Rings they let go unanswered with nobody else picking up, or declined">Missed</th>
                <th className="pd-num" title="Voicemails left in their mailbox">Voicemails</th>
                <th className="pd-num">Outgoing</th>
                <th className="pd-num" title="Talk time on the incoming calls they answered">On the phone</th>
                <th className="pd-num">Avg call</th>
                <th className="pd-num" title="Median time their phone rang before they picked up">Picks up in</th>
                {intakeColumns && <>
                  <th className="pd-num pd-intake-col" title="Answered ÷ (answered + missed by everyone + declined)">When rung</th>
                  <th className="pd-num pd-intake-col" title="The queue rang them and a teammate picked up first. Never counts against them.">Passed</th>
                  <th className="pd-num pd-intake-col" title="Average queue wait of the callers they answered">Caller waited</th>
                </>}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const results = Object.keys(AGENT_LABELS).filter((id) => p[id]).map((id) => `${p[id]} ${AGENT_LABELS[id].toLowerCase()}`);
                return (
                  <tr key={p.extension} className="is-clickable" onClick={() => openPerson(p.extension)}>
                    <td className="pd-nowrap">
                      <span className="pd-person">{p.name || `Ext ${p.extension}`}</span>
                      {p.intake && <span className="pd-tag">Intake</span>}
                    </td>
                    {p.empty ? <td className="pd-faint" colSpan={intakeColumns ? 10 : 7}>No calls in this range</td> : <>
                      <td className="pd-num">{count(p.answered)}</td>
                      <td className="pd-num">{count(p.missed_by_everyone) + count(p.declined)}</td>
                      <td className="pd-num">{count(p.voicemails)}</td>
                      <td className="pd-num">{count(p.outbound)}</td>
                      <td className="pd-num">{p.talk_seconds ? formatSeconds(p.talk_seconds) : '—'}</td>
                      <td className="pd-num">{formatSeconds(p.avg_talk_seconds)}</td>
                      <td className="pd-num">{formatSeconds(p.median_pickup_seconds)}</td>
                      {intakeColumns && (p.intake ? <>
                        <td className="pd-num pd-intake-col" title={results.join(' · ')}>
                          <strong>{p.answer_rate !== null ? `${p.answer_rate}%` : '—'}</strong>
                        </td>
                        <td className="pd-num pd-intake-col">{count(p.passed_quick) + count(p.passed_slow)}</td>
                        <td className="pd-num pd-intake-col">{formatSeconds(p.avg_queue_wait)}</td>
                      </> : <>
                        <td className="pd-num pd-intake-col pd-faint">—</td>
                        <td className="pd-num pd-intake-col pd-faint">—</td>
                        <td className="pd-num pd-intake-col pd-faint">—</td>
                      </>)}
                    </>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Definitions() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/admin/phone-dashboard/definitions').then(({ data: defs }) => setData(defs)).catch(() => setData({}));
  }, []);
  if (!data) return <div className="pd-center"><AppSpinner /></div>;
  const groups = [
    ['caller', 'Caller perspective', 'How every incoming call ended for the caller. Each call gets exactly one.'],
    ['intake', 'Intake perspective', 'What each intake agent\'s phone did for each intake call.'],
    ['scores', 'Scores and times', 'The numbers on the Overview and People views.'],
  ];
  return (
    <div className="pd-stack">
      {groups.map(([key, title, intro]) => (
        <section key={key} className="pd-card">
          <header className="pd-card__header"><div><h3>{title}</h3><span>{intro}</span></div></header>
          <dl className="pd-definitions">
            {(data[key] || []).map((item) => (
              <div key={item.term}><dt>{item.term}</dt><dd>{item.meaning}</dd></div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ calls

function Calls({ state, update, staffOptions }) {
  const showToast = useContext(ToastContext);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState(state.filters.q);
  const { filters, from, to, page } = state;
  const moreSet = MORE_FILTERS.filter((key) => filters[key]).length;
  const [more, setMore] = useState(moreSet > 0);

  useEffect(() => setText(filters.q), [filters.q]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = { from, to, page, page_size: PAGE_SIZE };
    FILTER_KEYS.forEach((key) => { if (filters[key]) params[key] = filters[key]; });
    api.get('/admin/phone-dashboard/calls', { params })
      .then(({ data }) => active && setResult(data))
      .catch((error) => active && showToast({ severity: 'error', summary: 'Search failed', detail: error?.response?.data?.error || 'Calls could not be loaded' }))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [filters, from, to, page, showToast]);

  const setFilter = (changes) => update({ ...changes, page: '' });
  const pages = result ? Math.max(1, Math.ceil(result.total / PAGE_SIZE)) : 1;
  const anyFilter = FILTER_KEYS.some((key) => filters[key]);
  const person = staffOptions.find((o) => o.value === filters.staff);
  const outcomes = [{ id: '', label: 'All', count: result?.counts?.all }, ...CALLER.map((c) => ({ ...c, count: result?.counts?.[c.id] }))]
    .filter((chip) => !['selfservice', 'disconnect'].includes(chip.id) || chip.count || filters.outcome === chip.id);

  return (
    <section className="pd-card">
      <form className="pd-picker" onSubmit={(event) => { event.preventDefault(); setFilter({ q: text.trim() }); }}>
        <span className="pd-search">
          <MagnifyingGlass size={16} className="pd-search__icon" aria-hidden="true" />
          <AppTextInput aria-label="Search calls" placeholder="Number, caller or client name" value={text}
            onChange={(event) => setText(event.target.value)} onBlur={() => text.trim() !== filters.q && setFilter({ q: text.trim() })} />
        </span>
        <span className="pd-outcomes" role="tablist" aria-label="How the call ended">
          {outcomes.map((chip) => (
            <button key={chip.id || 'all'} type="button" role="tab" aria-selected={filters.outcome === chip.id}
              className={`pd-btn${filters.outcome === chip.id ? ' is-active' : ''}`}
              onClick={() => setFilter({ outcome: chip.id })}>
              {chip.label} <span className="pd-btn__count">{chip.count ?? '…'}</span>
            </button>
          ))}
        </span>
        <button type="button" className={`pd-btn${more ? ' is-open' : ''}`} aria-expanded={more} onClick={() => setMore(!more)}>
          Filters{moreSet ? ` · ${moreSet}` : ''} <CaretDown size={12} aria-hidden="true" />
        </button>
      </form>
      {more && (
        <div className="pd-filters">
          <AppSelect value={filters.staff} placeholder="Anyone" options={staffOptions} onChange={(value) => setFilter({ staff: value })} />
          <AppSelect value={filters.direction} placeholder="In and out" options={DIRECTION_OPTIONS} onChange={(value) => setFilter({ direction: value, intake: value && value !== 'inbound' ? '' : filters.intake })} />
          {filters.intake && (
            <span className="pd-person-chip">
              {filters.intake === 'no_agent_free' ? 'No intake agent free' : 'Intake queue calls'}
              <button type="button" aria-label="Remove the intake queue filter" onClick={() => setFilter({ intake: '' })}><X size={12} /></button>
            </span>
          )}
          <label className="pd-switch">
            <AppToggle checked={!!filters.recorded} onChange={({ value }) => setFilter({ recorded: value })} ariaLabel="Calls with audio only" />
            <span>Has audio</span>
          </label>
          {anyFilter && (
            <AppButton variant="secondary" label="Clear" icon="pi pi-filter-slash"
              onClick={() => { setText(''); update(Object.fromEntries([...FILTER_KEYS, 'page'].map((k) => [k, '']))); }} />
          )}
        </div>
      )}

      {!result && loading ? <div className="pd-center"><AppSpinner /></div>
        : result && result.calls.length === 0 ? <div className="pd-pad"><AdminEmptyState title="No calls match" description="Try a wider date range or fewer filters." /></div>
        : result ? (
          <>
            <div className={`pd-scroll${loading ? ' is-loading' : ''}`}>
              <table className="pd-table pd-table--tight pd-calls">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Who</th>
                    <th>What happened</th>
                    {person && <th title="What this person's phone did for the call">{person.label.split(' (')[0]}'s phone</th>}
                    <th className="pd-num">Talk</th>
                  </tr>
                </thead>
                <tbody>
                  {result.calls.map((call, index) => {
                    const title = partyTitle(call);
                    const number = formatPhone(call.party_number);
                    const day = firmDay(call.start_time);
                    const newDay = index === 0 || day !== firmDay(result.calls[index - 1].start_time);
                    const dayName = formatDay(call.start_time);
                    return (
                      <React.Fragment key={call.id}>
                        {newDay && (
                          <tr className="pd-day-row">
                            <td colSpan={person ? 5 : 4}>
                              {dayName}{dayName !== formatDate(call.start_time) ? <span className="pd-faint"> · {formatDate(call.start_time)}</span> : null}
                            </td>
                          </tr>
                        )}
                        <tr className={`is-clickable${state.call === call.id ? ' is-selected' : ''}`} onClick={() => update({ call: call.id })}>
                          <td><span className="pd-when">
                            <Direction direction={call.direction} />
                            {formatClock(call.start_time)}
                          </span></td>
                          <td><span className="pd-who">
                            <span className="pd-person">{title}</span>
                            {title !== number && <span className="pd-faint">{number}</span>}
                            {call.direction === 'outbound' && call.staff_name && <span className="pd-faint">from {firstName(call.staff_name)}</span>}
                            {call.client && <ClientBadge client={call.client} compact />}
                          </span></td>
                          <td className="pd-fill"><StatusChip status={call.status} label={call.status_label} /></td>
                          {person && (
                            <td>{call.staff_result
                              ? <span className={`pd-ring pd-ring--${call.staff_result}`}>{AGENT_LABELS[call.staff_result]}</span>
                              : <span className="pd-muted">—</span>}</td>
                          )}
                          <td className="pd-num"><span className="pd-talk">
                            {call.talk_seconds ? formatSeconds(call.talk_seconds) : ''}
                            {call.recording_url || call.voicemail_link ? <SpeakerHigh size={15} className="pd-faint" aria-label="Has audio" /> : null}
                          </span></td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="pd-pager">
              <span>{result.total} call{result.total === 1 ? '' : 's'}</span>
              <AppButton variant="secondary" icon="pi pi-chevron-left" label="Newer" disabled={page <= 1} onClick={() => update({ page: page - 1 })} />
              <span>Page {page} of {pages}</span>
              <AppButton variant="secondary" icon="pi pi-chevron-right" label="Older" disabled={page >= pages} onClick={() => update({ page: page + 1 })} />
            </div>
          </>
        ) : null}
    </section>
  );
}

// ------------------------------------------------------------------ live

function useNow(intervalMs) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}

function useLiveCalls(active) {
  const [live, setLive] = useState(null);
  const [failed, setFailed] = useState(false);
  const busy = useRef(false);

  useEffect(() => {
    if (!active) return undefined;
    let current = true;
    const load = () => {
      if (busy.current || document.hidden) return;
      busy.current = true;
      api.get('/admin/phone-dashboard/live')
        .then(({ data }) => { if (current) { setLive(data); setFailed(false); } })
        .catch(() => current && setFailed(true))
        .finally(() => { busy.current = false; });
    };
    load();
    const timer = setInterval(load, LIVE_POLL_MS);
    return () => { current = false; clearInterval(timer); };
  }, [active]);

  return { live, failed };
}

function seconds(now, iso) {
  return (now - new Date(iso).getTime()) / 1000;
}

function LiveStatus({ live, failed }) {
  const now = useNow(1000);
  if (!live) return null;
  const refreshed = live.refreshed_at ? new Date(live.refreshed_at).getTime() : null;
  const stale = !refreshed || now - refreshed > LIVE_STALE_MS;
  return (
    <span className={`pd-live-status${stale ? ' is-stale' : ''}`}
      title={`Checked ${formatClock(live.checked_at, true)}`}>
      {refreshed ? `Refreshed by the phone server ${formatSeconds(seconds(now, live.refreshed_at))} ago` : 'The phone server has not refreshed this list yet'}
      {stale ? ' · it may be out of date' : ''}
      {failed ? ' · last check failed' : ''}
    </span>
  );
}

// Left-edge colour of a live box, by what the call is doing.
const LIVE_TONES = {
  talking: 'green', held: 'orange', voicemail: 'blue', conference: 'purple',
  ringing: 'orange', queue: 'orange', intake_router: 'orange',
};
const CONNECTED = new Set(['talking', 'held', 'voicemail', 'conference']);

function IntakeTeam({ team, now, openCall }) {
  if (!team.length) return null;
  const free = team.filter((agent) => agent.state === 'free').length;
  return (
    <section className="pd-card">
      <header className="pd-team__header">
        <h3>Intake team</h3>
        <span className="pd-muted">{free} of {team.length} free</span>
        <span className="pd-faint pd-team__note">A signed-out phone app still shows as free</span>
      </header>
      <ul className="pd-team">
        {team.map((agent) => {
          const content = (
            <>
              <span className="pd-team__name">{agent.name || `Ext ${agent.extension}`}</span>
              <span className={`pd-chip pd-chip--team-${agent.state}`}>
                {agent.label}{agent.since ? ` · ${formatSeconds(seconds(now, agent.since))}` : ''}
              </span>
            </>
          );
          return (
            <li key={agent.extension}>
              {agent.call_uuid ? (
                <button type="button" className={`pd-team__agent pd-team__agent--${agent.state}`} onClick={() => openCall(agent.call_uuid)}>{content}</button>
              ) : <span className={`pd-team__agent pd-team__agent--${agent.state}`}>{content}</span>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function LiveBox({ tone, name, timer, state, meta, wide, selected, onClick, children }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick}
      className={`pd-box pd-box--${tone || 'gray'}${wide ? ' pd-box--wide' : ''}${selected ? ' is-selected' : ''}`}>
      <span className="pd-box__top"><span className="pd-box__name">{name}</span><span className="pd-box__timer">{timer}</span></span>
      <span className="pd-box__state">{state}</span>
      {meta ? <span className="pd-box__meta">{meta}</span> : null}
      {children}
    </Tag>
  );
}

function joinMeta(parts) {
  return parts.filter(Boolean).reduce((all, part, index) => [...all, index ? ' · ' : null, part], []);
}

function CallBox({ call, now, selected, openCall }) {
  const title = partyTitle(call);
  const number = title !== formatPhone(call.party_number) ? formatPhone(call.party_number) : null;
  const keys = call.keys?.length ? `pressed ${call.keys.join(', then ')}` : null;
  const tags = [
    call.intake && CONNECTED.has(call.state) ? <span key="intake" className="pd-tag">Intake</span> : null,
    call.client ? <ClientBadge key="client" client={call.client} compact /> : null,
  ];
  const own = call.direction !== 'inbound';
  const who = call.staff_name || (call.staff_extension ? `Ext ${call.staff_extension}` : 'Staff');
  let state = call.state_label;
  let meta;
  let since = call.started_at;
  if (call.state === 'talking' || call.state === 'held') {
    since = call.answered_at || call.started_at;
    if (own) state = call.direction === 'outbound' ? `${who} called out` : `${who} · internal call`;
    const held = call.held_at ? `on hold ${formatTimer(seconds(now, call.held_at))}` : null;
    const waited = !own && call.answered_at ? `waited ${formatSeconds(seconds(new Date(call.answered_at).getTime(), call.started_at))}` : null;
    meta = joinMeta([number, held || (own ? 'Talking' : waited)]);
  } else {
    meta = joinMeta([number, lineName(call), keys]);
  }
  return (
    <LiveBox tone={LIVE_TONES[call.state]} name={title} timer={formatTimer(seconds(now, since))} state={state}
      selected={selected} onClick={() => openCall(call.call_uuid)}
      meta={<>{meta}{tags.some(Boolean) ? <>{' '}{tags}</> : null}</>} />
  );
}

function RoomBox({ calls, now, selected, openCall }) {
  const oldest = calls.reduce((a, b) => (a.started_at <= b.started_at ? a : b));
  return (
    <LiveBox tone="purple" wide name={calls[0].room_name} timer={formatTimer(seconds(now, oldest.started_at))}
      state={`${calls.length} on the call`}>
      <span className="pd-box__members">
        {calls.map((call) => (
          <button key={call.call_uuid} type="button" className={selected === call.call_uuid ? 'is-selected' : undefined}
            onClick={() => openCall(call.call_uuid)}>
            {partyTitle(call)}{' '}
            <span className="pd-muted">
              {joinMeta([partyTitle(call) !== formatPhone(call.party_number) ? formatPhone(call.party_number) : null,
                formatSeconds(seconds(now, call.started_at))])}
            </span>
          </button>
        ))}
      </span>
    </LiveBox>
  );
}

// Calls in the same conference room become one box, where the first of them would be.
function boxes(calls) {
  const rooms = new Map();
  const items = [];
  calls.forEach((call) => {
    if (call.state !== 'conference' || !call.room) {
      items.push({ key: call.call_uuid, call });
      return;
    }
    if (!rooms.has(call.room)) {
      const item = { key: `room-${call.room}`, room: [] };
      rooms.set(call.room, item);
      items.push(item);
    }
    rooms.get(call.room).room.push(call);
  });
  return items.map((item) => (item.room?.length === 1 ? { key: item.room[0].call_uuid, call: item.room[0] } : item));
}

function LiveSection({ title, hint, calls, now, selected, openCall }) {
  if (!calls.length) return null;
  return (
    <section className="pd-live-section">
      <header className="pd-section-title"><h4>{title} · {calls.length}</h4><span>{hint}</span></header>
      <div className="pd-boxes">
        {boxes(calls).map((item) => (item.room
          ? <RoomBox key={item.key} calls={item.room} now={now} selected={selected} openCall={openCall} />
          : <CallBox key={item.key} call={item.call} now={now} selected={selected === item.call.call_uuid} openCall={openCall} />))}
      </div>
    </section>
  );
}

function Live({ live, failed, selected, openCall }) {
  const now = useNow(1000);
  if (!live) {
    return failed
      ? <section className="pd-card pd-pad"><AdminEmptyState title="Live calls could not be loaded" description="Trying again every few seconds." /></section>
      : <div className="pd-center"><AppSpinner /></div>;
  }
  const waiting = live.calls.filter((call) => !CONNECTED.has(call.state));
  const connected = live.calls.filter((call) => CONNECTED.has(call.state));
  return (
    <div className="pd-stack">
      <IntakeTeam team={live.intake || []} now={now} openCall={openCall} />
      {live.calls.length === 0 ? (
        <section className="pd-card pd-empty"><h3>No calls in progress</h3><p className="pd-muted">New calls show up here within a few seconds.</p></section>
      ) : (
        <>
          <LiveSection title="Waiting" hint="oldest first" calls={waiting} now={now} selected={selected} openCall={openCall} />
          <LiveSection title="Connected" hint="click a box for the full call" calls={connected} now={now} selected={selected} openCall={openCall} />
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ page

const PRESETS = [
  { label: 'Today', days: 1 },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

function PhoneDashboard() {
  const showToast = useContext(ToastContext);
  const [state, update] = usePhoneDashboardParams();
  const [stats, setStats] = useState(null);
  const [staff, setStaff] = useState([]);
  const { from, to, view } = state;
  const { live, failed: liveFailed } = useLiveCalls(view === 'live');

  useEffect(() => {
    api.get('/admin/phone-dashboard/staff').then(({ data }) => setStaff(data.staff || [])).catch(() => setStaff([]));
  }, []);

  useEffect(() => {
    let active = true;
    setStats(null);
    api.get('/admin/phone-dashboard/stats', { params: { from, to } })
      .then(({ data }) => active && setStats(data))
      .catch((error) => active && showToast({ severity: 'error', summary: 'Stats failed', detail: error?.response?.data?.error || 'Call stats could not be loaded' }));
    return () => { active = false; };
  }, [from, to, showToast]);

  const staffOptions = useMemo(() => staff.map((p) => ({
    value: p.extension, label: `${p.name || 'Ext'} (${p.extension})${p.intake ? ' · intake' : ''}`,
  })), [staff]);

  const days = daysBetween(from, to);
  const activePreset = to === firmDate() ? PRESETS.find((p) => p.days === days) : null;
  const setRange = (nextFrom, nextTo) => update({ from: nextFrom, to: nextTo, page: '' });
  const step = (direction) => setRange(shiftDate(from, direction * days), shiftDate(to, direction * days));
  const openCalls = (filters) => update({ view: 'calls', ...Object.fromEntries(FILTER_KEYS.map((k) => [k, ''])), page: '', ...filters });
  const openPerson = (extension) => openCalls({ staff: extension });
  const closeCall = useCallback(() => update({ call: '' }), [update]);
  const closeLive = useCallback(() => update({ live: '' }), [update]);
  // A live call that ended opens as the finished call, in place of the live one.
  const liveEnded = useCallback((callId) => update({ live: '', call: callId }, { replace: true }), [update]);

  return (
    <div className="pd-page">
      <div className="pd-toolbar">
        <div className="pd-views" role="tablist" aria-label="Phone dashboard view">
          {VIEWS.map((v) => (
            <button key={v.id} type="button" role="tab" aria-selected={view === v.id}
              className={`pd-view${view === v.id ? ' is-active' : ''}`} onClick={() => update({ view: v.id })}>
              <v.Icon size={16} weight={view === v.id ? 'bold' : 'regular'} aria-hidden="true" />{v.label}
            </button>
          ))}
        </div>
        {view === 'live' && <LiveStatus live={live} failed={liveFailed} />}
        {view !== 'live' && view !== 'definitions' && <div className="pd-range">
          <div className="pd-presets">
            {PRESETS.map((p) => (
              <button key={p.label} type="button" className={`pd-preset${activePreset === p ? ' is-active' : ''}`}
                onClick={() => setRange(firmDate(-(p.days - 1)), firmDate())}>{p.label}</button>
            ))}
          </div>
          <button type="button" className="pd-icon-button" aria-label="Earlier" onClick={() => step(-1)}><CaretLeft size={14} /></button>
          <input type="date" aria-label="From" value={from} max={to} onChange={(e) => e.target.value && setRange(e.target.value, to)} />
          <span className="pd-muted">to</span>
          <input type="date" aria-label="To" value={to} min={from} onChange={(e) => e.target.value && setRange(from, e.target.value)} />
          <button type="button" className="pd-icon-button" aria-label="Later" disabled={to >= firmDate()} onClick={() => step(1)}><CaretRight size={14} /></button>
        </div>}
      </div>

      {view === 'live' ? (
        <Live live={live} failed={liveFailed} selected={state.live} openCall={(uuid) => update({ live: uuid })} />
      ) : view === 'definitions' ? (
        <Definitions />
      ) : view === 'calls' ? (
        <Calls state={state} update={update} staffOptions={staffOptions} />
      ) : !stats ? (
        <div className="pd-center"><AppSpinner /></div>
      ) : view === 'people' ? (
        <People stats={stats} staff={staff} openPerson={openPerson} />
      ) : (
        <Overview stats={stats} openCall={(id) => update({ call: id })}
          openCalls={(filters) => openCalls({ direction: 'inbound', ...filters })} />
      )}

      {state.call ? <PhoneCallModal callId={state.call} onClose={closeCall} /> : null}
      {state.live && !state.call ? (
        <LiveCallModal callUuid={state.live} pollMs={LIVE_POLL_MS} onClose={closeLive} onEnded={liveEnded} />
      ) : null}
    </div>
  );
}

export { formatPhone, formatSeconds };
export default PhoneDashboard;
